import pluralize from 'pluralize';
import { useMemo } from 'react';

import { type Notification, ShortUrlObjectType } from '@air/api/types';
import { type NotificationsItemProps } from '@air/component-notifications';
import { formatActorNameInitialsOrDefault } from '@air/utilities';

import { NotificationAvatar } from '~/components/AirNotifications/components/NotificationAvatar';
import { NotificationBaseItem } from '~/components/AirNotifications/components/NotificationBaseItem';
import { NotificationClip } from '~/components/AirNotifications/components/NotificationClip';
import { NotificationClips } from '~/components/AirNotifications/components/NotificationClips';
import { getShortUrlNotificationMeta } from '~/components/AirNotifications/utils/getShortUrlNotificationMeta';

export type NotificationClipDownloadedTypeProps = Pick<NotificationsItemProps, 'onClick'> & {
  notification: Notification<'clipDownloaded_userIsShareLinkCreator', 'account'>;
};

// The backend samples at most this many clips per notification, so at that count we
// can't tell whether it's the exact total or a capped total — say "several items" instead.
const MAX_SAMPLED_CLIPS = 5;

export const NotificationClipDownloadedType = ({ notification, onClick }: NotificationClipDownloadedTypeProps) => {
  const { actor, clips, count, shortUrl } = notification.data;

  const actionText = useMemo(() => {
    if (clips.length === 0) {
      return '';
    }

    if (clips.length === 1) {
      return clips[0].title + (clips[0].ext ? `.${clips[0].ext}` : '');
    }

    if (clips.length >= MAX_SAMPLED_CLIPS) {
      return 'several items';
    }

    return pluralize('item', clips.length, true);
  }, [clips]);

  const hasBoardName = shortUrl.objectType === ShortUrlObjectType.board;

  return (
    <NotificationBaseItem
      content={clips.length > 1 ? <NotificationClips clips={clips} totalClips={clips.length} /> : undefined}
      meta={getShortUrlNotificationMeta(shortUrl)}
      notification={notification}
      onClick={onClick}
      suffix={clips.length === 1 ? <NotificationClip clip={clips[0]} /> : undefined}
      prefix={<NotificationAvatar actor={actor} />}
      title={
        <>
          <b>{formatActorNameInitialsOrDefault(actor)}</b>
          {` downloaded `}
          <b>{actionText}</b>
          {count > 1 ? ` ${count} times` : ''}
          {hasBoardName && (
            <>
              {` in `}
              {shortUrl.objectName}
            </>
          )}
        </>
      }
    />
  );
};
